import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Mic, ShieldCheck, FileText } from 'lucide-react';

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, register, isLoading, error, clearError, isAuthenticated } = useAuth();

  const [isRegister, setIsRegister] = useState(
    searchParams.get('register') === 'true'
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');

  // If already authenticated, redirect to app
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/app');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    try {
      if (isRegister) {
        await register(email, password, fullName);
      } else {
        await login(email, password);
      }
      navigate('/app');
    } catch {
      // Error is handled in the store
    }
  };

  const bullets = [
    {
      icon: Mic,
      title: 'Voice-first dictation',
      description: 'Push-to-talk with real-time AI transcription',
    },
    {
      icon: FileText,
      title: 'Structured reports',
      description: 'AI generates properly formatted radiology reports',
    },
    {
      icon: ShieldCheck,
      title: 'Smart validation',
      description: 'Automatic laterality, measurement, and completeness checks',
    },
  ];

  return (
    <div className="min-h-screen flex">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-gray-50 flex-col justify-between p-12">
        <div>
          <Link to="/" className="flex items-center gap-2 mb-12">
            <div className="w-8 h-8 bg-teal-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">RD</span>
            </div>
            <span className="font-semibold text-gray-900 text-lg">
              Radiology Dashboard
            </span>
          </Link>

          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Your Voice. Your Report.
          </h2>
          <p className="text-2xl font-bold text-teal-600 mb-8">
            Perfected by AI.
          </p>

          <div className="space-y-6">
            {bullets.map((b) => (
              <div key={b.title} className="flex gap-4">
                <div className="w-10 h-10 bg-teal-50 border border-teal-200 rounded-lg flex items-center justify-center flex-shrink-0">
                  <b.icon className="text-teal-600" size={20} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 text-sm">
                    {b.title}
                  </h3>
                  <p className="text-sm text-gray-500">{b.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Dashboard screenshot */}
        <div className="mt-8 rounded-xl overflow-hidden border border-gray-200 shadow-sm">
          <img
            src="/screenshots/dashboard-hero.png"
            alt="Radiology Dashboard with AI-generated report"
            className="w-full h-auto"
          />
        </div>
      </div>

      {/* Right panel — form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-teal-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">RD</span>
              </div>
              <span className="font-semibold text-gray-900 text-lg">
                Radiology Dashboard
              </span>
            </Link>
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-1">
            {isRegister ? 'Create your account' : 'Welcome back'}
          </h1>
          <p className="text-gray-500 mb-8">
            {isRegister
              ? 'Get started with your free trial.'
              : 'Sign in to your dashboard.'}
          </p>

          {isRegister && (
            <div className="mb-6 flex items-center gap-2 px-3 py-2 bg-teal-50 border border-teal-200 rounded-lg">
              <span className="text-teal-700 text-sm font-medium">
                Start your free trial — 20 reports included
              </span>
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  placeholder="Dr. Jane Smith"
                  required
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                placeholder="you@example.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                placeholder="••••••••"
                required
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-2.5 px-4 bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white font-medium rounded-lg transition-colors"
            >
              {isLoading
                ? 'Loading...'
                : isRegister
                  ? 'Create Account'
                  : 'Sign In'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => {
                setIsRegister(!isRegister);
                clearError();
              }}
              className="text-sm text-teal-600 hover:text-teal-700 font-medium"
            >
              {isRegister
                ? 'Already have an account? Sign in'
                : "Don't have an account? Start free trial"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
